import { AnyCardRule, CardCompositeRule, CardRuleSet, CardTextRuleType } from "../../../models/CardRuleEngine";
import { CardVariant } from "../../../models/CardVariant";
import { opusStringToId } from "../../../models/Opus";
import { getDefaultRule } from "../../../models/Rule";
import { CARD_TEXT_RULES } from "../../CardRulesEngine";
import { CardFilterCommands } from "../CardFilterCommands";
import { IFilterCommand } from "../IFilterCommand";

export type SearchTargetRuleFilterArgs = {
    ruleSet: CardRuleSet | null | undefined
}

export class SearchTargetRuleFilter<TItem extends CardVariant> implements IFilterCommand<TItem, SearchTargetRuleFilterArgs> {
    isMatch(item: TItem, args: SearchTargetRuleFilterArgs = { ruleSet: null }): boolean {
        // No rule set was supplied, which means we're not filtering on a rule.
        if (!args.ruleSet) {
            return true;
        }

        // These values are not valid search targets.
        if (
            invalidOpus.includes(item.opus) ||
            invalidOpus.includes(item.altOpus ?? fakeOpus) ||
            item.card.isLB
        ) {
            return false;
        }

        let isMatch = args.ruleSet.operator === "and" ? true : false;

        for (const requirement of args.ruleSet.rules) {
            let requirementFulfilled = false;

            if (requirement.type === "composite") {
                requirementFulfilled = isCompositeRuleFulfilled({ rule: requirement, variant: item });
            } else {
                requirementFulfilled = isRequirementFulfilled({ requirement: requirement, variant: item });
            }

            isMatch = (args.ruleSet.operator === "and")
                ? (isMatch && requirementFulfilled)
                : (isMatch || requirementFulfilled);
        }

        return isMatch;
    }
}

const fakeOpus = -999;
const invalidOpus = [
    opusStringToId["A"],
    opusStringToId["B"],
    opusStringToId["C"]
];

function isCompositeRuleFulfilled(props: {
    rule: CardCompositeRule,
    variant: CardVariant
}) {
    let isMatch = props.rule.operator === "and" ? true : false;

    for (const requirement of props.rule.rules) {
        const requirementFulfilled = isRequirementFulfilled({ requirement: requirement, variant: props.variant });

        isMatch = (props.rule.operator === "and")
            ? (isMatch && requirementFulfilled)
            : (isMatch || requirementFulfilled);
    }

    return isMatch;
}

function isRequirementFulfilled(props: {
    requirement: AnyCardRule,
    variant: CardVariant
}) {
    switch (props.requirement.category) {
        case "CardBooleanRule": {
            const actualValue = props.variant.card[props.requirement.type];
            const isMatch = actualValue === props.requirement.value;
            return props.requirement.sign === "=" ? isMatch : !isMatch;
        }
        case "CardNumberRule": {
            const actualValue = props.variant.card[props.requirement.type];

            switch (props.requirement.sign) {
                case "!=":
                    return actualValue !== props.requirement.value;
                case "<":
                    return actualValue < props.requirement.value;
                case "<=":
                    return actualValue <= props.requirement.value;
                case "=":
                    return actualValue === props.requirement.value;
                case ">":
                    return actualValue > props.requirement.value;
                case ">=":
                    return actualValue >= props.requirement.value;
                default:
                    return false;
            }
        }
        case "CardStringArrayRule": {
            const actualValues: string[] = props.variant.card[props.requirement.type] ?? [];
            const isMatch = actualValues.includes(props.requirement.value);
            return props.requirement.sign === "=" ? isMatch : !isMatch;
        }
        case "CardStringRule": {
            const actualValue = props.variant.card[props.requirement.type] ?? "";
            const isMatch = (actualValue === props.requirement.value);
            return props.requirement.sign === "=" ? isMatch : !isMatch;
        }
        case "CardTextRule": {
            const isMatchFunction = CARD_TEXT_RULES[props.requirement.type];
            if (!isMatchFunction) {
                return false;
            }

            const isMatch = isMatchFunction(props.variant);
            return props.requirement.sign === "=" ? isMatch : !isMatch;
        }
        case "CompositeRule": {
            return false;
        }
        default: {
            return false;
        }
    }
}